import { safePublicHttpHref } from '@/lib/public-href'

export type CampaignSocialLinkKey =
  | 'instagram'
  | 'facebook'
  | 'youtube'
  | 'tiktok'
  | 'twitter'
  | 'linkedin'
  | 'website'

export type CampaignSocialLinks = Partial<Record<CampaignSocialLinkKey, string>>

export const CAMPAIGN_SOCIAL_LINK_FIELDS: ReadonlyArray<{
  key: CampaignSocialLinkKey
  label: string
  placeholder: string
  icon: string
}> = [
  {
    key: 'instagram',
    label: 'Instagram',
    placeholder: 'Ex: https://instagram.com/sua-pagina',
    icon: 'mdi:instagram',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    placeholder: 'Ex: https://facebook.com/sua-pagina',
    icon: 'mdi:facebook',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    placeholder: 'Ex: https://youtube.com/@seu-canal',
    icon: 'mdi:youtube',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    placeholder: 'Ex: https://tiktok.com/@seu-perfil',
    icon: 'mdi:tiktok',
  },
  {
    key: 'twitter',
    label: 'X (Twitter)',
    placeholder: 'Ex: https://x.com/seu-perfil',
    icon: 'mdi:twitter',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    placeholder: 'Ex: https://linkedin.com/company/sua-empresa',
    icon: 'mdi:linkedin',
  },
  {
    key: 'website',
    label: 'Site',
    placeholder: 'Ex: https://seusite.com.br',
    icon: 'mdi:web',
  },
]

/**
 * @param onlySafeHttp Na página pública, descarta URL que não seja http(s) (não vira `href`).
 *   No formulário do painel fica `false`: o backend valida e devolve o erro ao usuário.
 */
function normalizeSocialLinks(
  links: Record<string, string> | null | undefined,
  onlySafeHttp = false
): CampaignSocialLinks {
  if (!links) {
    return {}
  }

  const normalized: CampaignSocialLinks = {}

  for (const field of CAMPAIGN_SOCIAL_LINK_FIELDS) {
    const value = onlySafeHttp ? safePublicHttpHref(links[field.key]) : links[field.key]?.trim()
    if (value) {
      normalized[field.key] = value
    }
  }

  return normalized
}

export function resolveLandingSocialLinks(options: {
  campaignLinks?: Record<string, string> | null
  institutionLinks?: Record<string, string> | null
}): CampaignSocialLinks {
  const campaign = normalizeSocialLinks(options.campaignLinks ?? null, true)
  const institution = normalizeSocialLinks(options.institutionLinks ?? null, true)

  const merged: CampaignSocialLinks = { ...institution }

  for (const field of CAMPAIGN_SOCIAL_LINK_FIELDS) {
    const campaignValue = campaign[field.key]
    if (campaignValue) {
      merged[field.key] = campaignValue
    }
  }

  return merged
}

export function hasLandingSocialLinks(links: CampaignSocialLinks): boolean {
  return CAMPAIGN_SOCIAL_LINK_FIELDS.some((field) => Boolean(links[field.key]))
}

export function sanitizeCampaignSocialLinksInput(
  links: CampaignSocialLinks | null | undefined
): CampaignSocialLinks {
  return normalizeSocialLinks(links ?? null)
}
